import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import { RootState } from '@cp/src/store';
import { createKey } from '@cp/src/utils/CaseDetail.utils';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader.js';
import AddressCard from './AddressCard';
import { Button, Typography, Chip } from '@navi/web-ui/lib/primitives';
import { AddIcon, ArrowDownIcon, ArrowUpIcon, CircularLoaderIcon } from '@navi/web-ui/lib/icons';
import { fetchTopAddresses } from '../../actions/topAddressesActions';
import { ActiveFilters, Address, AddressFilterType, INITIAL_FILTERS } from './interfaces';
import { filterAddresses } from './utils';
import Modal from '@cp/src/components/Modal/Modal';
import AddNewAddressForm from '../Addresses/AddNewAddressForm';

const TopAddresses: React.FC = () => {
  const { loanId = '', customerId = '', caseReferenceId = '' } = useParams();
  const dispatch = useDispatch();

  // State
  const [showOtherAddresses, setShowOtherAddresses] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(INITIAL_FILTERS);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Selectors
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );
  const showAddNewAddress = useSelector((state: RootState) => state.common.isCiuAgent);

  // Derived state
  const { data, loading: addressesLoading } = pageData?.topAddresses || {};
  const { unifiedLocations } = data || {};

  const filteredLocations = useMemo(() => {
    if (!unifiedLocations) return [];
    return filterAddresses(unifiedLocations, activeFilters);
  }, [unifiedLocations, activeFilters]);

  const topAddresses = filteredLocations.slice(0, 5);
  const otherAddresses = filteredLocations.slice(5);

  // Handlers
  const toggleFilter = (filterType: AddressFilterType) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  const handleLonghornFilterClick = () => toggleFilter('LONGHORN');
  const handleCustomerOutreachFilterClick = () => toggleFilter('CUSTOMER_OUTREACH');

  const handleAddAddressModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const toggleOtherAddresses = () => {
    setShowOtherAddresses(prev => !prev);
  };

  // Effects
  useEffect(() => {
    dispatch(fetchTopAddresses(loanId, customerId, caseReferenceId));
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Addresses_PageLand);
  }, []);

  // Empty state
  if (!addressesLoading && (!unifiedLocations || unifiedLocations.length === 0)) {
    return (
      <div className={`${styles.mainContainer} justify-center items-center`}>
        <Typography variant="h4">No addresses found</Typography>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <SuspenseLoader
        loading={!!addressesLoading}
        fallBack={
          <div className="flex justify-center items-center h-full">
            <CircularLoaderIcon size="xl" />
          </div>
        }
      >
        {/* Header Section */}
        <div className="flex justify-between items-center mt-4 px-5">
          <div className="flex items-center gap-2">
            <Typography variant="h4">Top addresses</Typography>
            {showAddNewAddress && (
              <Button
                variant="text"
                onClick={handleAddAddressModal}
                startAdornment={<AddIcon color="var(--blue-base)" />}
              >
                Add new address
              </Button>
            )}
          </div>

          {/* Filter Chips */}
          <div className="flex items-center gap-3">
            <Typography variant="p3" className="text-[var(--grayscale-content-2)]">
              Show only
            </Typography>
            <div className="flex items-center gap-2">
              <Chip
                label="Longhorn"
                size="small"
                wrapperClasses={styles.filterChip}
                selected={activeFilters.LONGHORN}
                onChipClick={handleLonghornFilterClick}
              />
              <Chip
                label="Customer outreach"
                size="small"
                wrapperClasses={styles.filterChip}
                selected={activeFilters.CUSTOMER_OUTREACH}
                onChipClick={handleCustomerOutreachFilterClick}
              />
            </div>
          </div>
        </div>

        {/* Top Addresses Grid */}
        <div className={styles.addressGrid}>
          {topAddresses?.map((address: Address) => (
            <div
              key={`${address.referenceId}-${activeFilters.LONGHORN}-${activeFilters.CUSTOMER_OUTREACH}`}
            >
              <AddressCard address={address} />
            </div>
          ))}
        </div>

        {/* View Other Addresses Toggle */}
        <Button
          variant="text"
          onClick={toggleOtherAddresses}
          className={styles.viewOtherAddresses}
          disabled={!otherAddresses.length}
        >
          <div className={styles.viewOtherAddresses}>
            View other addresses
            {showOtherAddresses ? (
              <ArrowUpIcon color="var(--navi-color-blue-base)" />
            ) : (
              <ArrowDownIcon color="var(--navi-color-blue-base)" />
            )}
          </div>
        </Button>

        {/* Other Addresses Grid */}
        {showOtherAddresses && (
          <div className={styles.addressGrid}>
            {otherAddresses?.map((address: Address) => (
              <div
                key={`${address.referenceId}-${activeFilters.LONGHORN}-${activeFilters.CUSTOMER_OUTREACH}`}
              >
                <AddressCard address={address} />
              </div>
            ))}
          </div>
        )}

        {/* Add Address Modal */}
        <Modal
          onClose={handleAddAddressModal}
          visible={isModalOpen}
          header="Add new address"
          headerClassName="w-[380px] !py-6 !pr-3"
        >
          <AddNewAddressForm loanId={loanId} handleAddAddressModal={handleAddAddressModal} />
        </Modal>
      </SuspenseLoader>
    </div>
  );
};

export default TopAddresses;
